import type { Grid } from "../core/Grid.ts";
import type { Pathfinding, PathGrid } from "../core/Pathfinding.ts";
import type { Vec2 } from "../core/types.ts";
import { CellType } from "../core/types.ts";

export type PlacementKind = "tower" | "firewall" | "signal";

/**
 * Single source of truth for placement legality: cell-type eligibility
 * AND route reachability. Every placement/preview path MUST gate on this.
 *
 * Modeled on StealthGuard.ts — one predicate, one source, no scattered copies.
 *
 * Cell-type rules (current behavior, per kind):
 *   tower:    EMPTY, PATH_RESERVED, or HIDDEN_SPAWN
 *   signal:   EMPTY, PATH_RESERVED, or HIDDEN_SPAWN
 *   firewall: EMPTY only
 *
 * Route reachability: blocking (col, row) must not cut off any entry→exit path.
 */
export function canPlaceAt(
  grid: Grid,
  pathfinding: Pathfinding,
  entries: Vec2[],
  exits: Vec2[],
  col: number,
  row: number,
  kind: PlacementKind,
): boolean {
  if (!grid.isInBounds(col, row)) return false;

  const cell = grid.getCell(col, row);
  if (!cell) return false;

  // ─── Cell-type eligibility (per kind) ──────────────────
  if (!isCellEligible(cell.type, kind)) return false;

  // ─── Route reachability ────────────────────────────────
  const pathGrid: PathGrid = {
    width: grid.width,
    height: grid.height,
    isBlocked: (gx: number, gy: number) => {
      if (gx === col && gy === row) return true;
      return grid.getCell(gx, gy)?.blocksPath ?? false;
    },
  };
  return pathfinding.canPlace(pathGrid, entries, exits, col, row);
}

/** Cell-type eligibility check, split out for testability. */
export function isCellEligible(
  cellType: CellType,
  kind: PlacementKind,
): boolean {
  switch (kind) {
    case "signal":
      return (
        cellType === CellType.EMPTY ||
        cellType === CellType.PATH_RESERVED ||
        cellType === CellType.HIDDEN_SPAWN
      );
    case "tower":
      return (
        cellType === CellType.EMPTY ||
        cellType === CellType.PATH_RESERVED ||
        cellType === CellType.HIDDEN_SPAWN
      );
    case "firewall":
      return cellType === CellType.EMPTY;
  }
}
